import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SEO from "@/components/SEO";
import { SEO_KEYWORDS } from "@/seo/seoData";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ChevronDown, Search, HelpCircle, ArrowRight } from "lucide-react";
const HERO_IMAGE = "/faq.webp";

const faqGroups = [
  {
    category: "General",
    questions: [
      {
        question: "What does Square Trigger do?",
        answer: (
          <>
            Square Trigger is a digital agency in Vancouver offering{" "}
            <Link
              to="/services/website-development"
              className="text-primary hover:underline"
            >
              website development
            </Link>
            ,{" "}
            <Link
              to="/services/seo-services"
              className="text-primary hover:underline"
            >
              SEO services
            </Link>
            ,{" "}
            <Link
              to="/services/social-media-marketing"
              className="text-primary hover:underline"
            >
              social media marketing
            </Link>
            ,{" "}
            <Link
              to="/services/custom-tool-creation"
              className="text-primary hover:underline"
            >
              custom tool creation
            </Link>
            , and ongoing{" "}
            <Link
              to="/services/maintenance-support"
              className="text-primary hover:underline"
            >
              maintenance &amp; support
            </Link>
            .
          </>
        ),
        schemaAnswer:
          "Square Trigger is a digital agency in Vancouver offering website development, SEO services, social media marketing, custom tool creation, and ongoing maintenance & support.",
      },
      {
        question: "Do you only work with businesses in Vancouver?",
        answer: (
          <>
            No. We are based in Vancouver, but we work with businesses across
            Canada and support clients remotely.
          </>
        ),
        schemaAnswer:
          "No. We are based in Vancouver, but we work with businesses across Canada and support clients remotely.",
      },
      {
        question: "What industries do you work with?",
        answer: (
          <>
            We have strong experience in insurance and automotive, and we also
            work with businesses that need better websites, stronger visibility,
            clearer messaging, or smarter digital systems.
          </>
        ),
        schemaAnswer:
          "We have strong experience in insurance and automotive, and we also work with businesses that need better websites, stronger visibility, clearer messaging, or smarter digital systems.",
      },
    ],
  },
  {
    category: "Services",
    questions: [
      {
        question: "Can I hire you for just one service?",
        answer: (
          <>
            Yes. Some clients need only one service, while others need a
            combination of{" "}
            <Link
              to="/services/website-development"
              className="text-primary hover:underline"
            >
              website development
            </Link>
            ,{" "}
            <Link
              to="/services/seo-services"
              className="text-primary hover:underline"
            >
              SEO
            </Link>
            ,{" "}
            <Link
              to="/services/social-media-marketing"
              className="text-primary hover:underline"
            >
              social media marketing
            </Link>
            ,{" "}
            <Link
              to="/services/custom-tool-creation"
              className="text-primary hover:underline"
            >
              custom tools
            </Link>
            , or{" "}
            <Link
              to="/services/maintenance-support"
              className="text-primary hover:underline"
            >
              support
            </Link>
            .
          </>
        ),
        schemaAnswer:
          "Yes. Some clients need only one service, while others need a combination of website development, SEO, social media marketing, custom tools, or support.",
      },
      {
        question: "Do you build custom websites?",
        answer: (
          <>
            Yes. We build websites that are designed around your business goals,
            user flow, and growth needs.
          </>
        ),
        schemaAnswer:
          "Yes. We build websites that are designed around your business goals, user flow, and growth needs.",
      },
      {
        question: "Do you offer SEO services for existing websites?",
        answer: (
          <>
            Yes. We can improve{" "}
            <Link
              to="/services/seo-services"
              className="text-primary hover:underline"
            >
              SEO services
            </Link>{" "}
            on an existing website by working on structure, content, internal
            linking, and technical issues.
          </>
        ),
        schemaAnswer:
          "Yes. We can improve SEO on an existing website by working on structure, content, internal linking, and technical issues.",
      },
      {
        question: "Do you create custom tools or dashboards?",
        answer: (
          <>
            Yes. We build custom web applications,{" "}
            <Link
              to="/services/custom-tool-creation"
              className="text-primary hover:underline"
            >
              dashboards
            </Link>
            ,{" "}
            <Link
              to="/services/custom-tool-creation"
              className="text-primary hover:underline"
            >
              calculators
            </Link>
            , and workflow-based digital tools for specific business needs.
          </>
        ),
        schemaAnswer:
          "Yes. We build custom web applications, dashboards, calculators, and workflow-based digital tools for specific business needs.",
      },
      {
        question: "Do you offer website maintenance after launch?",
        answer: (
          <>
            Yes. We provide ongoing{" "}
            <Link
              to="/services/maintenance-support"
              className="text-primary hover:underline"
            >
              maintenance and support
            </Link>{" "}
            to help keep websites updated, secure, and working properly.
          </>
        ),
        schemaAnswer:
          "Yes. We provide ongoing maintenance and support to help keep websites updated, secure, and working properly.",
      },
    ],
  },
  {
    category: "Process",
    questions: [
      {
        question: "How do you start a project?",
        answer: (
          <>
            We start by understanding the business, the problem, and what needs
            to improve before recommending the right solution.
          </>
        ),
        schemaAnswer:
          "We start by understanding the business, the problem, and what needs to improve before recommending the right solution.",
      },
      {
        question: "How long does a project take?",
        answer: (
          <>
            It depends on the service and scope. Smaller projects move faster,
            while larger websites or custom builds take more time. We provide a
            clearer timeline after the discovery stage.
          </>
        ),
        schemaAnswer:
          "It depends on the service and scope. Smaller projects move faster, while larger websites or custom builds take more time. We provide a clearer timeline after the discovery stage.",
      },
      {
        question: "Do you use templates?",
        answer: (
          <>
            No. We build around what the business actually needs rather than
            forcing a one-size-fits-all package.
          </>
        ),
        schemaAnswer:
          "No. We build around what the business actually needs rather than forcing a one-size-fits-all package.",
      },
    ],
  },
  {
    category: "Contact & Working Together",
    questions: [
      {
        question: "How do I get started?",
        answer: (
          <>
            You can contact us through the form on the{" "}
            <Link to="/contact" className="text-primary hover:underline">
              Contact page
            </Link>{" "}
            and share what you need. We&apos;ll review it and guide you to the
            right next step.
          </>
        ),
        schemaAnswer:
          "You can contact us through the form on the Contact page and share what you need. We’ll review it and guide you to the right next step.",
      },
      {
        question: "Do you offer consultations?",
        answer: (
          <>
            Yes. You can reach out through the{" "}
            <Link to="/contact" className="text-primary hover:underline">
              Contact page
            </Link>{" "}
            to discuss your business needs and the best starting point.
          </>
        ),
        schemaAnswer:
          "Yes. You can reach out through the Contact page to discuss your business needs and the best starting point.",
      },
      {
        question: "Do you work remotely?",
        answer: (
          <>
            Yes. We are a remote-first agency based in Vancouver and support
            clients through online communication and digital collaboration.
          </>
        ),
        schemaAnswer:
          "Yes. We are a remote-first agency based in Vancouver and support clients through online communication and digital collaboration.",
      },
    ],
  },
];

export default function FAQsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [openIndex, setOpenIndex] = useState(null);
  const [email, setEmail] = useState("");

  const filteredFAQs = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return faqGroups.flatMap((group) =>
      group.questions
        .filter((item) => {
          if (!query) return true;
          return (
            item.question.toLowerCase().includes(query) ||
            item.schemaAnswer.toLowerCase().includes(query) ||
            group.category.toLowerCase().includes(query)
          );
        })
        .map((item) => ({
          ...item,
          category: group.category,
        }))
    );
  }, [searchQuery]);

  return (
    <Layout>
     <SEO
  title="Digital Agency FAQs: Answers to Top Questions | Square Trigger"
  description="Find answers to the most Frequently Asked Questions about our Digital Agency's complete services, pricing, and more. Read more."
  ogTitle="Digital Agency FAQs: Answers to Top Questions | Square Trigger"
  ogDescription="Find answers to the most Frequently Asked Questions about our Digital Agency's complete services, pricing, and more. Read more."
  ogSiteName="Square Trigger FAQs"
  ogImage="https://squaretrigger.com/logo3.webp"
  keywords={SEO_KEYWORDS.faqs}
/>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
  <div className="absolute inset-0">
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${HERO_IMAGE})` }}
    />
    <div className="absolute inset-0 bg-gradient-to-br from-foreground/80 via-foreground/70 to-foreground/60" />
  </div>

        <div className="container-custom relative z-10 px-5 pt-[20px] pb-[44px] sm:px-5 sm:pt-[90px] sm:pb-[56px] lg:pt-[60px] lg:pb-[88px]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto max-w-[900px] text-center"
          >
            <div className="mb-5 flex justify-center sm:mb-3">
              <Badge className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/20 px-3 py-2 text-primary-foreground sm:px-4">
                <HelpCircle className="h-4 w-4" />
                <span className="text-sm font-medium">FAQs</span>
              </Badge>
            </div>

            <h1 className='mx-auto mb-[18px] max-w-[760px] font-["Lato",system-ui,sans-serif] text-[clamp(2.2rem,4vw,3.6rem)] font-extrabold leading-[1.12] text-background'>
              Frequently Asked <span className="gradient-text">Questions</span>
            </h1>

            <p className="mx-auto mb-8 max-w-[720px] text-center text-[1.08rem] font-medium leading-[1.7] text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)]">
  Find quick answers about our services, process, timelines, and how
  Square Trigger works with businesses.
</p>

            <div className="mb-8 flex flex-col items-stretch justify-center gap-[14px] sm:flex-row sm:items-center">
              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full justify-center sm:w-auto"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>

              <Button
                variant="glass"
                size="lg"
                asChild
                className="w-full justify-center border border-white/20 bg-white/10 text-white transition-all duration-300 hover:-translate-y-[1px] hover:bg-white/10 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
              >
                <Link to="/services/website-development">
                  View Our Services
                </Link>
              </Button>
            </div>

            <div className="relative mx-auto max-w-[640px]">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-background/50" />
              <Input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-12 border-white/20 bg-white/10 pl-12 text-white placeholder:text-white/50"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-10 h-72 w-72 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute -bottom-24 right-10 h-72 w-72 rounded-full bg-secondary/5 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto mb-10 max-w-[920px] text-center sm:mb-12"
          >
            <h2 className="text-3xl font-display font-bold text-foreground md:text-4xl">
              What Businesses Usually Ask First
            </h2>
            <p className="mx-auto mt-4 max-w-[760px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              Browse the most common questions about our services, timelines,
              working style, and project process.
            </p>
          </motion.div>

          <div className="mx-auto w-full max-w-[1540px] px-0 sm:px-0">
            <div className="rounded-[28px] border border-border/70 bg-card/40 px-5 py-8 shadow-soft backdrop-blur-sm sm:px-8 sm:py-10 lg:px-12 lg:py-12">
              {filteredFAQs.length === 0 ? (
                <div className="rounded-2xl border border-border bg-background px-6 py-12 text-center">
                  <HelpCircle className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
                  <p className="text-muted-foreground">
                    No matching questions found.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredFAQs.map((faq, index) => (
                    <motion.div
                      key={`${faq.category}-${faq.question}`}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.04 }}
                      className="relative z-10 overflow-hidden rounded-xl border border-border bg-background"
                    >
                      <button
                        type="button"
                        onPointerUp={() =>
                          setOpenIndex(openIndex === index ? null : index)
                        }
                        className="pointer-events-auto flex w-full touch-manipulation items-center justify-between p-5 text-left transition-colors hover:bg-accent/50 sm:p-6"
                      >
                        <div className="pr-8">
                          <Badge variant="outline" className="mb-2 text-xs">
                            {faq.category}
                          </Badge>
                          <span className="block font-semibold text-foreground">
                            {faq.question}
                          </span>
                        </div>

                        <ChevronDown
                          className={`h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform duration-300 ${
                            openIndex === index ? "rotate-180" : ""
                          }`}
                        />
                      </button>

                      <div
                        className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                          openIndex === index
                            ? "grid-rows-[1fr]"
                            : "grid-rows-[0fr]"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <div className="px-5 pb-5 leading-relaxed text-muted-foreground sm:px-6 sm:pb-6">
                            {faq.answer}
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              mainEntity: faqGroups.flatMap((group) =>
                group.questions.map((faq) => ({
                  "@type": "Question",
                  name: faq.question,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: faq.schemaAnswer,
                  },
                }))
              ),
            })}
          </script>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-[28px] bg-gradient-to-r from-[#7c1fff] via-[#4c4cf4] to-[#25a9f5] px-6 py-14 text-center shadow-soft sm:px-10 sm:py-16 lg:px-16 lg:py-20"
          >
            <div className="pointer-events-none absolute inset-0">
              <div className="absolute left-0 top-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
              <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-white/10 blur-3xl sm:h-96 sm:w-96" />
            </div>

            <div className="relative z-10 mx-auto max-w-[1000px]">
              <h2 className="mb-4 text-3xl font-display font-bold text-primary-foreground sm:mb-6 md:text-4xl">
                Still Have Questions?
              </h2>

              <p className="mx-auto mb-8 max-w-[860px] text-base leading-relaxed text-primary-foreground/85 sm:text-lg">
                Tell us what your business needs and we&apos;ll help you figure
                out the best next step.
              </p>

              <div className="flex flex-col justify-center gap-3 sm:flex-row sm:gap-4">
                <Button
                  variant="premium"
                  size="lg"
                  asChild
                  className="w-full justify-center bg-background text-foreground hover:bg-background/90 sm:w-auto"
                >
                  <Link
                    to="/contact"
                    className="flex items-center justify-center gap-2"
                  >
                    Contact Us
                    <ArrowRight className="h-5 w-5" />
                  </Link>
                </Button>

                <Button
                  variant="glass"
                  size="lg"
                  asChild
                  className="w-full justify-center border border-white/20 bg-white/10 text-white transition-all duration-300 hover:-translate-y-[1px] hover:bg-white/15 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
                >
                  <Link to="/contact">Book a Free Consultation</Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Newsletter */}
      {/* <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mx-auto w-full max-w-[1540px]"
          >
            <div className="rounded-[28px] border border-border/70 bg-card/40 px-6 py-10 text-center shadow-soft backdrop-blur-sm sm:px-8 sm:py-12 lg:px-12 lg:py-14">
              <h2 className="mb-4 text-3xl font-display font-bold text-foreground md:text-4xl">
                Stay Updated With Digital Insights
              </h2>

              <p className="mx-auto mb-8 max-w-[760px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                Get useful updates on website development, SEO, digital
                strategy, and business growth.
              </p>

              <form
                onSubmit={(e) => e.preventDefault()}
                className="mx-auto flex max-w-[720px] flex-col gap-3 sm:flex-row"
              >
                <Input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-12"
                />
                <Button
                  type="submit"
                  variant="gradient"
                  size="lg"
                  className="w-full sm:w-auto"
                >
                  Subscribe
                </Button>
              </form>
            </div>
          </motion.div>
        </div>
      </section> */}
    </Layout>
  );
}